import { AnchorProvider, Program } from "@coral-xyz/anchor";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { aprToApy, fromLamports } from "@stepfinance/common";
import type { LockedStakingPosition } from "@stepfinance/staking";
import { useEffect, useState } from "react";

import { confirmOptions } from "../../constants/transaction";
import { useMarkets } from "../../contexts/market";
import { UXDStakingAccountJSON } from "../../models/uxd/idl";
import { UXD_STAKING_CAMPAIGN_PDA, UXD_STAKING_PROGRAM_ID, UXP_MINT } from "../../models/uxd/ids";
import { useConnectedWallet } from "../wallet/useConnectedWallet";

const STAKING_PERIOD_OPTIONS = [
  { seed: 1, durationDays: 14 },
  { seed: 2, durationDays: 30 },
  { seed: 3, durationDays: 90 },
];

export const useUXDStaking = () => {
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { marketsInitialised, priceInUSD } = useMarkets();
  const [stakingPositions, setStakingPositions] = useState<LockedStakingPosition[]>([]);

  useEffect(() => setStakingPositions([]), [wallet]);

  useEffect(() => {
    const fetchStakingPositions = async () => {
      if (wallet && marketsInitialised) {
        const stakingPositionsList: LockedStakingPosition[] = [];
        const provider = new AnchorProvider(connection, wallet, confirmOptions);
        const program = new Program(UXDStakingAccountJSON, UXD_STAKING_PROGRAM_ID, provider);

        for (const option of STAKING_PERIOD_OPTIONS) {
          const stakingPDA = PublicKey.findProgramAddressSync(
            [UXD_STAKING_CAMPAIGN_PDA.toBuffer(), wallet.publicKey.toBuffer(), Buffer.from(Uint8Array.of(option.seed))],
            UXD_STAKING_PROGRAM_ID,
          )[0];

          const stakeAccountData = await program.account.stakingAccount.fetchNullable(stakingPDA);

          if (!stakeAccountData) {
            continue;
          }

          const { stakedAmount, rewardAmount, stakeEndTs } = stakeAccountData;

          const uiStakedAmount = fromLamports(stakedAmount, 9);

          if (!uiStakedAmount) {
            continue;
          }

          const uiRewardAmount = fromLamports(rewardAmount, 9);
          const uxpPrice = priceInUSD(UXP_MINT.toBase58()) ?? 0;

          const apr = (uiRewardAmount / uiStakedAmount) * (365 / option.durationDays) * 100;
          const frequency = 365 / option.durationDays;
          const apy = aprToApy(apr, frequency) / 100;

          stakingPositionsList.push({
            kind: "UXD",
            link: "https://stake.uxd.fi/",
            apy: apy,
            stakingMint: UXP_MINT,
            amountStaked: uiStakedAmount,
            rewardMint: UXP_MINT,
            pendingReward: uiRewardAmount,
            pendingRewardUSD: uiRewardAmount * uxpPrice,
            valueInUSD: uiStakedAmount * uxpPrice,
            stakeDuration: option.durationDays,
            stakeEndTs: stakeEndTs.toNumber(),
          });
        }
        return stakingPositionsList;
      }
    };
    void fetchStakingPositions().then((result) => result && setStakingPositions(result));
  }, [connection, wallet, priceInUSD, marketsInitialised]);

  return stakingPositions;
};
